import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import LeadScoreRing from './LeadScoreRing';
import InlineCallRecorder from './InlineCallRecorder';
import {
  Search, Phone, Loader2, ChevronLeft, Sparkles, Clock, Flame,
} from 'lucide-react';
import { cn } from '../lib/utils';

const STAGE_LABELS = {
  new_lead: 'New', contacted: 'Contacted', discovery: 'Discovery',
  educating: 'Educating', pre_qualifying: 'Pre-Qual',
  ready_to_proceed: 'Ready', long_term_nurture: 'Long-term',
  lost: 'Lost', converted: 'Converted',
};

/**
 * QuickLogCallDialog
 *
 * Press `C` anywhere in the workspace (or tap the FAB on mobile) to open this.
 * Two-step flow:
 *   1. Search/pick a lead (or "Quick — no lead" mode for unknown caller)
 *   2. Inline recorder → transcript auto-attached to the chosen lead's activity log
 *
 * Designed mobile-first — full-screen on small screens, modal on desktop.
 */
const QuickLogCallDialog = ({ open, onClose, leads = [], authToken, onLogged }) => {
  const [step, setStep] = useState('pick');           // 'pick' | 'record'
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStep('pick');
      setSearch('');
      setPicked(null);
      // Defer focus to next tick so the dialog's animation completes
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const live = (leads || []).filter((l) => !['lost', 'converted'].includes(l.status));
    if (!q) {
      // Show top 8 by recent contact / score
      return [...live]
        .sort((a, b) => {
          const aT = a.last_contacted_at ? new Date(a.last_contacted_at).getTime() : 0;
          const bT = b.last_contacted_at ? new Date(b.last_contacted_at).getTime() : 0;
          if (bT !== aT) return bT - aT;
          return (b.lead_score || 0) - (a.lead_score || 0);
        })
        .slice(0, 8);
    }
    return live.filter((l) => {
      const hay = `${l.name || ''} ${l.phone || ''} ${l.email || ''} ${l.lead_number || ''}`.toLowerCase();
      return hay.includes(q);
    }).slice(0, 12);
  }, [leads, search]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 p-0 overflow-hidden flex flex-col h-[100vh] sm:h-auto sm:max-h-[90vh] max-w-full sm:max-w-2xl rounded-none sm:rounded-2xl"
        data-testid="quick-log-call-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-200 dark:border-slate-800">
          <DialogTitle className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50 flex items-center gap-2">
            {step === 'record' && (
              <button
                onClick={() => { setStep('pick'); setPicked(null); }}
                className="text-slate-500 hover:text-slate-900 dark:hover:text-slate-100 transition"
                data-testid="quick-log-back-btn"
                aria-label="Back to lead picker"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <Phone className="w-5 h-5 text-emerald-500" />
            Quick log call
          </DialogTitle>
          <p className="text-xs font-workspace-body text-slate-500 dark:text-slate-400 mt-1">
            {step === 'pick'
              ? 'Pick the lead you’re on a call with — recording starts on the next screen.'
              : `Recording for ${picked?.name || 'the selected lead'}.`}
          </p>
        </DialogHeader>

        {step === 'pick' && (
          <div className="flex flex-col flex-1 min-h-0">
            <div className="px-5 pt-4 pb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <Input
                  ref={inputRef}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, phone, email or lead #"
                  className="pl-9 bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 font-workspace-body h-11"
                  data-testid="quick-log-search"
                />
              </div>
              <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500 font-workspace-body">
                <Sparkles className="w-3 h-3" />
                {search ? `${filtered.length} match${filtered.length === 1 ? '' : 'es'}` : 'Recent + top-scoring leads'}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1">
              {filtered.length === 0 ? (
                <div className="text-center py-12 text-slate-500 font-workspace-body text-sm">
                  No leads match.
                </div>
              ) : (
                filtered.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => { setPicked(l); setStep('record'); }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-all min-h-[64px] active:scale-[0.99]"
                    data-testid={`quick-log-pick-${l.id}`}
                  >
                    <LeadScoreRing
                      score={l.lead_score || 0}
                      name={l.name}
                      temperature={l.lead_temperature || 'Cold'}
                      size={44}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-display font-semibold text-slate-900 dark:text-slate-50 truncate">{l.name || 'Unknown'}</span>
                        {l.lead_temperature === 'Hot' && (
                          <Flame className="w-3 h-3 text-orange-500 flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[11px] font-workspace-body text-slate-500 dark:text-slate-400">
                        <Badge variant="outline" className="px-1.5 py-0 text-[10px] border-slate-300 dark:border-slate-700">
                          {STAGE_LABELS[l.status] || l.status}
                        </Badge>
                        {l.phone && <span className="truncate">{l.phone}</span>}
                      </div>
                    </div>
                    {l.last_contacted_at && (
                      <div className="text-[10px] text-slate-400 flex items-center gap-1 flex-shrink-0">
                        <Clock className="w-3 h-3" />
                        last
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>

            <div className="hidden sm:flex border-t border-slate-200 dark:border-slate-800 px-5 py-3 items-center justify-between text-[10px] uppercase tracking-wider text-slate-500 font-workspace-body">
              <span>Tip: Press <kbd className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-mono">C</kbd> anywhere to open this</span>
              <span>Esc to close</span>
            </div>
          </div>
        )}

        {step === 'record' && picked && (
          <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
            <div className="flex items-center gap-3 p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800">
              <LeadScoreRing
                score={picked.lead_score || 0}
                name={picked.name}
                temperature={picked.lead_temperature || 'Cold'}
                size={56}
              />
              <div className="min-w-0">
                <div className="font-display text-xl font-bold tracking-tight text-slate-900 dark:text-slate-50 truncate">{picked.name}</div>
                <div className="text-xs font-workspace-body text-slate-500 dark:text-slate-400 truncate">
                  {picked.lead_number} · {STAGE_LABELS[picked.status] || picked.status} · {picked.phone || picked.email || '—'}
                </div>
              </div>
            </div>

            <InlineCallRecorder
              leadId={picked.id}
              leadName={picked.name}
              authToken={authToken}
              onTranscribed={() => {
                onLogged?.(picked);
              }}
            />

            <div className="text-[11px] text-slate-500 font-workspace-body text-center pt-2">
              Tap the mic to start. The transcript auto-attaches to <span className="font-semibold text-slate-700 dark:text-slate-300">{picked.name}</span>’s activity log.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuickLogCallDialog;
